import React from 'react';

import { ChartsReferenceLine } from '@mui/x-charts';
import {
    useDrawingArea,
    useSvgRef,
    useXScale,
    useYScale,
} from '@mui/x-charts/hooks';

import { AxisId } from 'ff/debrief/models';
import { useChrono } from 'ff/debrief/hooks';
import AirplaneProfileIcon from 'ff/debrief/components/customIcons/AirplaneProfileIcon';

interface Props {
    currentTime: Date;
    currentAltitude: number;
    withAirplane?: boolean;
}

const Scrubber = (props: React.PropsWithChildren<Props>) => {
    const svgRef = useSvgRef();
    const xScale = useXScale<'time'>();
    const yScale = useYScale(
        props.withAirplane ? ('altitude' as AxisId) : null
    );
    const chrono = useChrono();
    const drawingArea = useDrawingArea();

    const scaleId =
        xScale.invert(drawingArea.left).toString() +
        xScale.invert(drawingArea.left + drawingArea.width).toString() +
        svgRef.current?.getBoundingClientRect().width.toString();

    const isDragging = React.useRef(false);

    const handleSetTime = (evt: MouseEvent) => {
        const svg = evt.currentTarget as SVGSVGElement;
        const viewportPoint = svg.createSVGPoint(); // Create an SVGPoint

        // Set the point's coordinates to the event's client position
        viewportPoint.x = evt.clientX;
        viewportPoint.y = evt.clientY;

        // Transform the point to the SVG coordinate system
        const svgPoint = viewportPoint.matrixTransform(
            svg.getScreenCTM().inverse()
        );

        // Set xValue within bounds of drawing area
        if (drawingArea.isPointInside(svgPoint)) {
            chrono.setTime(xScale.invert(svgPoint.x));
        }
    };

    const handleMouseDown = (evt: MouseEvent) => {
        isDragging.current = true;
        handleSetTime(evt);
    };
    const handleMouseMove = (evt: MouseEvent) => {
        if (!isDragging.current) return;
        handleSetTime(evt);
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    React.useEffect(() => {
        const svgEl = svgRef.current;
        svgEl.addEventListener('mousedown', handleMouseDown);
        svgEl.addEventListener('mousemove', handleMouseMove);
        svgEl.addEventListener('mouseup', handleMouseUp);
        return () => {
            svgEl.removeEventListener('mousedown', handleMouseDown);
            svgEl.removeEventListener('mousemove', handleMouseMove);
            svgEl.removeEventListener('mouseup', handleMouseUp);
        };
    }, [svgRef, scaleId]);

    if (
        drawingArea.isPointInside({
            x: xScale(props.currentTime),
            y: drawingArea.bottom,
        })
    ) {
        return (
            <>
                <ChartsReferenceLine x={props.currentTime} />
                {props.withAirplane && (
                    <AirplaneProfileIcon
                        x={xScale(props.currentTime) - 12}
                        y={yScale(props.currentAltitude) - 15}
                    />
                )}
            </>
        );
    } else {
        return null;
    }
};

export default Scrubber;
